import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { DEV_VERSION } from "@/lib/constants";
import type { Session, User } from "@supabase/supabase-js";

interface LoginScreenProps {
  onLogin: (session: Session, user: User) => void;
}

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePwMsg, setChangePwMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    setError("");

    try {
      if (!supabase) {
        setError("Database not configured. Contact your admin.");
        setLoading(false);
        return;
      }

      const signInPromise = supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 15000)
      );

      const { data, error: authError } = await Promise.race([signInPromise, timeoutPromise]);

      if (authError) {
        setError(
          authError.message === "Invalid login credentials"
            ? "Invalid email or password. Please try again."
            : authError.message
        );
        setShaking(true);
        setTimeout(() => setShaking(false), 500);
        setLoading(false);
        return;
      }

      if (data?.session) {
        onLogin(data.session, data.user);
      }
    } catch (err) {
      const msg = err instanceof Error && err.message === "timeout"
        ? "Connection timed out — the database may be waking up. Try again in a moment."
        : "Unable to connect. Please try again.";
      setError(msg);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePwMsg("");
    if (!newPassword.trim() || newPassword.length < 6) {
      setChangePwMsg("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePwMsg("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      if (!supabase) { setChangePwMsg("Database not configured."); setLoading(false); return; }
      const result = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (result.error) {
        setChangePwMsg("Current email/password is incorrect.");
        setLoading(false);
        return;
      }

      const updateResult = await supabase.auth.updateUser({ password: newPassword });
      if (updateResult.error) {
        setChangePwMsg("Failed to update password: " + updateResult.error.message);
        setLoading(false);
        return;
      }

      setChangePwMsg("Password updated! Signing you in...");
      setTimeout(() => {
        onLogin(result.data.session!, result.data.user);
      }, 1000);
    } catch {
      setChangePwMsg("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center login-bg ff-primary h-screen w-screen">
      <div className={`login-card login-card-box ${shaking ? "shake" : ""}`}>
        {/* Logo */}
        <div className="mb-3 text-center">
          <img src="/branding/pxmdash-logo-stacked.png?v=2" alt="PXM Dash — Product Content Studio" className="w-260 h-auto" />
        </div>

        <div className="divider-line" />

        {!showChangePassword ? (
          <>
            <div className="text-lg text-mid mb-10 lh-relaxed ff-secondary">
              Sign in with your email and password.
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-7">
                <label className="login-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(""); }}
                  placeholder="you@ubrands.com"
                  autoFocus
                  autoComplete="email"
                  className={`login-input ${error ? 'login-input-error' : ''}`}
                  onFocus={e => { if (!error) e.currentTarget.style.borderColor = "#31363d"; }}
                  onBlur={e => { if (!error) e.currentTarget.style.borderColor = "#EBE7E4"; }}
                />
              </div>

              <div className="mb-7">
                <label className="login-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className={`login-input ${error ? 'login-input-error' : ''}`}
                  onFocus={e => { if (!error) e.currentTarget.style.borderColor = "#31363d"; }}
                  onBlur={e => { if (!error) e.currentTarget.style.borderColor = "#EBE7E4"; }}
                />
                {error && (
                  <div className="text-md text-rose mt-3 fw-500 ff-secondary">
                    {error}
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading || !email.trim() || !password.trim()} className={`login-submit-base ${(email.trim() && password.trim()) ? 'login-submit-active' : 'login-submit-inactive'} ${loading ? 'opacity-70' : ''}`}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button onClick={() => setShowChangePassword(true)} className="no-border pointer text-md text-slate fw-600 bg-none ff-secondary">Change Password</button>
            </div>

            <div className="text-center text-sm text-light mt-6 ff-secondary">
              Need access? Contact your team admin.
            </div>
          </>
        ) : (
          <>
            <div className="text-lg text-mid mb-10 lh-relaxed ff-secondary">
              Enter your current credentials and choose a new password.
            </div>

            <form onSubmit={handleChangePassword}>
              <div className="mb-7">
                <label className="login-label">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@ubrands.com" autoComplete="email" className="login-input" />
              </div>
              <div className="mb-7">
                <label className="login-label">Current Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Current password" autoComplete="current-password" className="login-input" />
              </div>
              <div className="mb-7">
                <label className="login-label">New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters" autoComplete="new-password" className="login-input" />
              </div>
              <div className="mb-7">
                <label className="login-label">Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" className="login-input" />
                {changePwMsg && (
                  <div className={`text-md mt-3 fw-500 ff-secondary ${changePwMsg.includes("updated") ? 'login-change-pw-success' : 'login-change-pw-error'}`}>
                    {changePwMsg}
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading} className={`login-submit-base login-submit-active ${loading ? 'opacity-70' : ''}`}>
                {loading ? "Updating..." : "Update Password"}
              </button>

              <div className="mt-4 text-center">
                <button onClick={() => { setShowChangePassword(false); setChangePwMsg(""); }} className="no-border pointer text-md text-light bg-none ff-secondary">Back to Sign In</button>
              </div>
            </form>
          </>
        )}

        {/* Branding footer */}
        <div className="flex justify-between items-center login-footer">
          <span className="text-xs text-light ls-normal ff-secondary">{DEV_VERSION}</span>
          <span className="text-xs text-light ff-secondary">U Brands</span>
        </div>
      </div>
    </div>
  );
}
